/**
 * Mémoire de retour du shell V2 (règle « Revenir, c'est retrouver ») : pour
 * chaque page, la dernière adresse complète visitée dans l'onglet (recherche,
 * filtres, tri, vue… quand la page les écrit dans son adresse) et la position
 * de défilement. Le fil d'Ariane et les liens « Retour aux … » visent cette
 * adresse au lieu de l'adresse nue ; un retour restaure le défilement.
 *
 * Elle retient aussi la fiche d'origine : arrivé sur une fiche en cliquant
 * depuis une autre fiche (enclos → individu), le fil d'Ariane montre cette
 * fiche et la rouvre dans l'onglet où on l'a quittée.
 *
 * Gardée dans `sessionStorage` : propre à l'onglet, perdue à sa fermeture.
 * Toute lecture ou écriture du stockage peut échouer (navigation privée,
 * stockage bloqué) : la mémoire continue alors en mémoire vive seulement.
 */

export const RETURN_MEMORY_KEY = "akv2:return-memory";
const MAX_ENTRIES = 80;

const safeSessionStorage = () => {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch (_error) {
    return null;
  }
};

const splitHref = (href) => {
  const text = String(href || "");
  const hashIndex = text.indexOf("#");
  const withoutHash = hashIndex >= 0 ? text.slice(0, hashIndex) : text;
  const queryIndex = withoutHash.indexOf("?");
  return queryIndex >= 0
    ? { pathname: withoutHash.slice(0, queryIndex), search: withoutHash.slice(queryIndex) }
    : { pathname: withoutHash, search: "" };
};

const keyOf = ({ pathname = "", search = "" } = {}) => `${pathname}${search || ""}`;

// Adresses qui ouvrent un objet ou une création sur le chemin d'une liste
// (`/v2/tasks?taskId=…`, `?mode=create`, dossier d'un suivi BEA
// `/v2/objectives?targetType=…&targetId=…`) : ce n'est pas l'état de la liste,
// on garde la dernière adresse de liste connue.
const isListState = (search) => {
  const parameters = new URLSearchParams(search || "");
  const opensFollowUp = parameters.has("targetType") && parameters.has("targetId");
  return !parameters.has("taskId") && parameters.get("mode") !== "create" && !opensFollowUp;
};

export const createReturnMemory = (storage = safeSessionStorage(), now = () => Date.now()) => {
  let state = { pages: {}, scroll: {}, records: {}, origins: {} };
  try {
    const saved = JSON.parse(storage?.getItem(RETURN_MEMORY_KEY) || "null");
    if (saved && typeof saved === "object") {
      state = {
        pages: saved.pages || {},
        scroll: saved.scroll || {},
        records: saved.records || {},
        origins: saved.origins || {},
      };
    }
  } catch (_error) {
    // Mémoire illisible : on repart d'une mémoire vide.
  }
  let pendingReturn = null;
  // Adresse affichée juste avant (mémoire vive : un rechargement n'a pas de
  // provenance).
  let previous = null;

  const trim = (record) => Object.fromEntries(
    Object.entries(record)
      .sort(([, left], [, right]) => (right.at || 0) - (left.at || 0))
      .slice(0, MAX_ENTRIES)
  );
  const persist = () => {
    state = {
      pages: trim(state.pages),
      scroll: trim(state.scroll),
      records: trim(state.records),
      origins: trim(state.origins),
    };
    try {
      storage?.setItem(RETURN_MEMORY_KEY, JSON.stringify(state));
    } catch (_error) {
      // Stockage plein ou bloqué : la mémoire vive suffit pour l'onglet.
    }
  };

  return Object.freeze({
    /** Retient l'adresse complète d'une page de l'interface V2. */
    remember(location) {
      const pathname = location?.pathname || "";
      if (!pathname.startsWith("/v2/") || !isListState(location.search)) return;
      state.pages[pathname] = { search: location.search || "", at: now() };
      persist();
    },
    /**
     * Arrivée sur une adresse (`action` du routeur : PUSH, REPLACE, POP).
     * Un clic depuis une fiche vers une autre page fait de cette fiche
     * l'origine de la page (elle ne s'affiche que si la page est une fiche) ;
     * un clic depuis une page qui n'est pas une fiche (menu, liste,
     * recherche) efface l'origine. Le précédent du navigateur, un
     * rechargement ou un changement d'onglet de la même fiche la gardent, et
     * revenir à la fiche d'origine par son lien ne la fait pas changer
     * d'origine.
     */
    arrive(location, action, { documentNavigation } = {}) {
      const pathname = location?.pathname || "";
      const from = previous;
      previous = pathname ? { pathname, search: location.search || "" } : null;
      if (!pathname.startsWith("/v2/")) return;
      // Première adresse du document : un rechargement ou le précédent du
      // navigateur gardent l'origine ; une adresse saisie, un nouvel onglet
      // ou un lien venu d'ailleurs l'effacent.
      if (!from) {
        if (documentNavigation === "navigate" && state.origins[pathname]) {
          delete state.origins[pathname];
          persist();
        }
        return;
      }
      if (from.pathname === pathname || action === "POP") return;
      const record = state.records[from.pathname];
      if (record && state.origins[from.pathname]?.pathname === pathname) return;
      if (record) {
        state.origins[pathname] = {
          pathname: from.pathname,
          href: `${from.pathname}${from.search}`,
          title: record.title,
          trail: record.trail,
          at: now(),
        };
      } else {
        delete state.origins[pathname];
      }
      persist();
    },
    /** Adresse affichée (chemin et paramètres), ou `null`. */
    currentLocation() {
      return previous;
    },
    /**
     * Retient une fiche affichée : son titre et sa hiérarchie (ancêtres du
     * fil d'Ariane, sans la fiche d'origine éventuelle).
     */
    rememberRecord(pathname, { title, trail = [] } = {}) {
      if (typeof pathname !== "string" || !pathname.startsWith("/v2/") || !title) return;
      state.records[pathname] = {
        title,
        trail: trail.map(({ destinationId, href, label }) => ({ destinationId, href: href || null, label })),
        at: now(),
      };
      // La fiche d'origine suit la dernière adresse de la fiche quittée
      // (onglet ouvert), retenue par `remember`.
      persist();
    },
    /** Fiche d'origine d'une page : `{ title, href, trail }` ou `null`. */
    originFor(pathname) {
      const origin = state.origins[pathname];
      if (!origin) return null;
      const remembered = state.pages[origin.pathname]?.search;
      return {
        title: origin.title,
        href: remembered !== undefined ? `${origin.pathname}${remembered}` : origin.href,
        trail: origin.trail || [],
      };
    },
    /** Retient la position de défilement de l'adresse affichée. */
    rememberScroll(location, top) {
      if (!location?.pathname?.startsWith("/v2/") || !Number.isFinite(top)) return;
      state.scroll[keyOf(location)] = { top: Math.max(0, Math.round(top)), at: now() };
      persist();
    },
    scrollFor(location) {
      return state.scroll[keyOf(location)]?.top ?? null;
    },
    /**
     * Adresse de retour d'un lien : l'adresse nue d'une page reprend la
     * dernière adresse complète visitée ; une adresse qui porte déjà ses
     * paramètres est gardée telle quelle.
     */
    returnHref(href) {
      if (typeof href !== "string" || !href.startsWith("/")) return href;
      const { pathname, search } = splitHref(href);
      if (search) return href;
      const remembered = state.pages[pathname]?.search;
      return remembered ? `${pathname}${remembered}` : href;
    },
    /** Annonce qu'une navigation est un retour : le défilement sera restauré. */
    markReturn(href) {
      pendingReturn = typeof href === "string" ? keyOf(splitHref(href)) : null;
    },
    /** Lit (et consomme) l'annonce de retour pour l'adresse affichée. */
    takeReturn(location) {
      const isReturn = pendingReturn !== null && pendingReturn === keyOf(location);
      pendingReturn = null;
      return isReturn;
    },
  });
};

export const returnMemory = createReturnMemory();

// Une page qui reprend la main sur son défilement (ancrage d'une liste qui
// reçoit des éléments en tête) demande au shell de lâcher la position tenue.
const scrollReleaseListeners = new Set();
export const onScrollRestoreRelease = (listener) => {
  scrollReleaseListeners.add(listener);
  return () => scrollReleaseListeners.delete(listener);
};
export const releaseScrollRestore = () => {
  scrollReleaseListeners.forEach((listener) => listener());
};

/**
 * Cible d'un lien de retour (« Retour aux … », fil d'Ariane) : la dernière
 * adresse complète de la page, annoncée comme un retour.
 */
export const returnLocation = (href) => {
  const target = returnMemory.returnHref(href);
  returnMemory.markReturn(target);
  return target;
};
